import React, { useState, useRef, useEffect } from "react";
import { Paperclip, Send, X } from "lucide-react";
import { Button, Input } from "../../components/ui";

const MessageInput = ({
  onSendMessage,
  onUploadFile,
  uploading = false,
  onTypingStart,
  onTypingStop,
}) => {
  const [content, setContent] = useState("");
  const [attachedFile, setAttachedFile] = useState(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  const stopTyping = () => {
    if (isTypingRef.current) {
      isTypingRef.current = false;
      onTypingStop?.();
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  };

  const handleTypingActivity = () => {
    if (!onTypingStart) return;

    if (!isTypingRef.current) {
      isTypingRef.current = true;
      onTypingStart();
    }

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      stopTyping();
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const uploadedData = await onUploadFile(file);
      setAttachedFile(uploadedData);
    } catch (err) {
      alert("File upload failed: " + (err.message || "Unknown error"));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSend = (e) => {
    e.preventDefault();

    if (!content.trim() && !attachedFile) return;

    stopTyping();

    let type = "TEXT";
    if (attachedFile) {
      type = attachedFile.fileType === "IMAGE" ? "IMAGE" : "DOCUMENT";
    }

    onSendMessage({
      type,
      content: content.trim(),
      file: attachedFile || undefined,
    });

    setContent("");
    setAttachedFile(null);
  };

  return (
    <div className="bg-white dark:bg-neutral-900 border-t border-neutral-200/60 p-3 sm:p-4 shrink-0">
      {attachedFile && (
        <div className="flex items-center justify-between bg-neutral-100 dark:bg-neutral-800 border border-neutral-200/80 rounded-xl px-3 py-2 mb-3 text-xs text-neutral-800 dark:text-neutral-100">
          <span className="truncate max-w-[240px] sm:max-w-md font-medium">📎 {attachedFile.originalName}</span>
          <button
            type="button"
            onClick={() => setAttachedFile(null)}
            className="text-neutral-400 dark:text-neutral-500 hover:text-rose-600 transition p-1"
            title="Remove attachment"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <form onSubmit={handleSend} className="flex items-center gap-2">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
        />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="p-2.5 text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-full shrink-0"
          title="Attach File"
          leftIcon={<Paperclip className="w-4 h-4" />}
        />

        <div className="flex-1">
          <Input
            type="text"
            placeholder={uploading ? "Uploading file..." : "Type a message..."}
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              if (e.target.value.trim()) {
                handleTypingActivity();
              } else {
                stopTyping();
              }
            }}
            onBlur={stopTyping}
            disabled={uploading}
            className="text-xs rounded-full py-2.5"
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={uploading || (!content.trim() && !attachedFile)}
          className="bg-neutral-900 hover:bg-neutral-800 text-white p-2.5 rounded-full shrink-0 flex items-center justify-center h-10 w-10"
        >
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
};

export default MessageInput;